import { useMemo } from 'react'

import { GasParams } from 'types/entities'

import { useNativeCurrency } from 'hooks/token'
import { useGasFetcher, useGetBalancesRequest } from 'hooks/api'

import { useIsSelfGenerated } from 'state/airdrop/hooks'
import { useSelfGeneratedAddress } from 'state/user/hooks'

import { errors } from 'constants/errors'

import { useBalances } from '../wallet'
import { useAirdropState } from './useAirdropState'

export function useSelfGeneratedState() {
  const isSelfGenerated = useIsSelfGenerated()
  const nativeCurr = useNativeCurrency()
  const privateKeyAddress = useSelfGeneratedAddress()

  const { approximateCost, getApproximateCost } = useAirdropState()

  const getGasParams = useGasFetcher()
  const balances = useBalances(privateKeyAddress)
  const balancesRequest = useGetBalancesRequest()

  const isSelfGeneratedBalanceEnough = useMemo(() => {
    return BigInt(balances.nativeBalance) >= approximateCost
  }, [balances.nativeBalance, approximateCost])

  const shouldTopUpPk = useMemo(() => {
    return isSelfGenerated && !isSelfGeneratedBalanceEnough
  }, [isSelfGenerated, isSelfGeneratedBalanceEnough])

  const getShouldTopUpPk = async (gasParams: GasParams) => {
    if (!privateKeyAddress) {
      return false
    }
    const { data: _balances } = await balancesRequest.refetch()
    const nativeBalance = _balances?.[privateKeyAddress]?.[nativeCurr.address]?.bgBalance

    if (!nativeBalance) {
      throw new Error(errors.rpc.REFETCH_REQUEST)
    }

    const _gasPrice = (gasParams?.maxFeePerGas ?? gasParams?.gasPrice) as bigint

    const _approximateCost = getApproximateCost(_gasPrice)

    const _isSelfGeneratedBalanceEnough = BigInt(nativeBalance) >= _approximateCost

    return isSelfGenerated && !_isSelfGeneratedBalanceEnough
  }

  const isLoading = useMemo(() => getGasParams.isLoading, [getGasParams.isLoading])

  return useMemo(
    () => ({
      isLoading,
      shouldTopUpPk,
      getShouldTopUpPk,
    }),
    [isLoading, shouldTopUpPk, getShouldTopUpPk],
  )
}
